import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Loader2,
  FileText,
  XCircle,
  CheckCircle2,
  PenTool,
  Trash2,
  Send,
  Calendar,
  User
} from "lucide-react";
import ReactMarkdown from "react-markdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ContractSigningPage - Public page for clients to sign contracts
 * Accessed via /sign/:token (no auth required)
 */
export default function ContractSigningPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  // Setup canvas when signature panel opens
  useEffect(() => {
    if (showSignature && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      ctx.scale(2, 2);
      
      // Set drawing style
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [showSignature]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/contracts/sign/${token}`);
      setData(res.data);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        setError("This signing request was not found.");
      } else if (status === 410) {
        setError(e.response?.data?.detail || "This signing request is no longer valid.");
      } else {
        setError("Unable to load contract. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const submitSignature = async () => {
    if (!hasSignature || !canvasRef.current) {
      toast.error("Please draw your signature first");
      return;
    }
    
    setSigning(true);
    try {
      // Get signature as base64
      const signatureData = canvasRef.current.toDataURL('image/png');
      
      await axios.post(`${API}/contracts/sign/${token}`, {
        signature_data: signatureData
      });
      
      setSigned(true);
      toast.success("Contract signed successfully!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit signature");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-600 mb-2">Unable to Sign</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-600 mb-3">Contract Signed!</h1>
          <p className="text-slate-400">
            Thank you for signing the contract. A copy will be sent to you via email.
            You can now close this page.
          </p>
        </div>
      </div>
    );
  }

  const { contract, project, client_name, freelancer_name, expires_at } = data || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6 lg:p-8" data-testid="contract-signing-page">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-700 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center">
                <FileText className="w-6 h-6 text-slate-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-600">{project?.title || "Contract Agreement"}</h1>
                <p className="text-sm text-slate-400">Version {contract?.version || 1}</p>
              </div>
            </div>
            <span className="text-xs bg-slate-800 text-slate-600 px-3 py-1 rounded-full">
              Awaiting Signature
            </span>
          </div>
          
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-400">
            {freelancer_name && (
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                From: {freelancer_name}
              </span>
            )}
            {expires_at && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                Expires: {new Date(expires_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Contract Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-700 p-6 sm:p-8 mb-6">
          <article className="prose pamber-slate max-w-none 
            pamber-headings:text-slate-600 pamber-headings:font-medium
            pamber-h1:text-2xl pamber-h1:border-b pamber-h1:border-slate-700 pamber-h1:pb-3
            pamber-h2:text-lg pamber-h2:text-slate-400 pamber-h2:mt-8
            pamber-p:text-slate-600 pamber-p:leading-relaxed
            pamber-li:text-slate-600
            pamber-strong:text-slate-600">
            <ReactMarkdown>{contract?.md_body || ""}</ReactMarkdown>
          </article>
        </div>

        {/* Signature Section */}
        {!showSignature ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-700 p-6 text-center">
            <PenTool className="w-10 h-10 text-indigo-500 mx-auto mb-4" />
            <h2 className="text-lg font-medium text-slate-600 mb-2">Ready to Sign?</h2>
            <p className="text-slate-400 text-sm mb-6">
              By signing this contract, you agree to the terms and conditions outlined above.
            </p>
            <button
              onClick={() => setShowSignature(true)}
              className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
              data-testid="start-signing-btn"
            >
              <PenTool className="w-5 h-5" />
              Sign Contract
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-700 p-6">
            <h2 className="text-lg font-medium text-slate-600 mb-4 flex items-center gap-2">
              <PenTool className="w-5 h-5 text-indigo-500" />
              Draw Your Signature
            </h2>
            
            {/* Signature Canvas */}
            <div className="relative mb-4">
              <canvas
                ref={canvasRef}
                className="w-full h-40 border-2 border-dashed border-slate-600 rounded-xl cursor-crosshair bg-slate-50"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              {!hasSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-indigo-400 text-sm">Sign here</p>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <button
                onClick={clearSignature}
                className="flex items-center gap-2 text-slate-400 hover:text-slate-400 px-4 py-2"
              >
                <Trash2 className="w-4 h-4" />
                Clear
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSignature(false)}
                  className="px-4 py-2 text-slate-400 hover:text-slate-400"
                >
                  Cancel
                </button>
                <button
                  onClick={submitSignature}
                  disabled={!hasSignature || signing}
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-indigo-500 px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
                  data-testid="submit-signature-btn"
                >
                  {signing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Signing...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Signature
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <p className="text-xs text-indigo-400 mt-4 text-center">
              By signing, you acknowledge that this electronic signature is legally binding.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-indigo-400">
            Powered by <Link to="/" className="text-indigo-500 hover:text-slate-400">ClientKit</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
